import React, {
	useState,
	useRef,
	useCallback,
	useEffect,
	type KeyboardEvent,
	type ElementRef,
	memo,
	type ClipboardEvent,
	useImperativeHandle,
	type PropsWithChildren,
	useMemo,
} from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import Avatar from '@atlaskit/avatar';
import AvatarGroup from '@atlaskit/avatar-group';
import Popup, { type TriggerProps } from '@atlaskit/popup'; // ignore-for-ENGHEALTH-17759
import { Flex, Box, xcss, Pressable } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type { UserOption } from '@atlassian/jira-base-user-picker/src/types.tsx';
import { CellWrapper as CellWrapperNew } from '@atlassian/jira-business-fields/src/common/cell-wrapper/index.tsx';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/src/common/ui/fixit-error-flag/index.tsx';
import { PopupComponent } from '@atlassian/jira-business-fields/src/common/ui/popup/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { DEFAULT_ROW_HEIGHT } from '../../../common/constants.tsx';
import { CellContainer, AvatarItem, DisplayName } from '../../../common/ui/list-cell/styled.tsx';
import UserPicker from './async-user-picker/index.tsx';
import messages from './messages.tsx';
import type { MultiUserCellProps } from './types.tsx';
import { isUserOptionsEqual, transformToUserOption } from './utils.tsx';

const MENU_MIN_WIDTH = 260;
const MAX_AVATAR = 5;

const POPUP_OFFSET: [number, number] = [0, 1 - DEFAULT_ROW_HEIGHT];
const POPUP_OFFSET_NEW: [number, number] = [0, 6 - DEFAULT_ROW_HEIGHT];

type MultiUserReadViewProps = {
	isEditable: boolean;
	userOptions: UserOption[] | null;
};

export const MultiUserReadView = memo<MultiUserReadViewProps>(
	({ isEditable, userOptions }: MultiUserReadViewProps) => {
		const { formatMessage } = useIntl();

		const content = (
			<>
				{userOptions && userOptions.length > 0 ? (
					<AvatarGroup
						appearance="stack"
						data={userOptions.map((user) => ({
							name: user.name,
							src: user.avatarUrl ?? '',
						}))}
						maxCount={MAX_AVATAR}
						size="small"
					/>
				) : (
					<AvatarItem isUnassigned>
						<Avatar appearance="circle" src="" size="small" />
						<DisplayName>{formatMessage(messages.emptyMultiUser)}</DisplayName>
					</AvatarItem>
				)}
			</>
		);

		if (fg('jira_list_keyboard_nav')) {
			return (
				<AvatarGroupContainer
					isEditable={isEditable}
					testId="business-list.ui.list-view.multi-user-cell.avatar-group-container"
				>
					{content}
				</AvatarGroupContainer>
			);
		}

		return (
			<AvatarGroupContainerOld
				isEditable={isEditable}
				data-testid="business-list.ui.list-view.multi-user-cell.avatar-group-container"
			>
				{content}
			</AvatarGroupContainerOld>
		);
	},
);

const MultiUserCell = memo<MultiUserCellProps>(
	({
		initialValue: _initialValue,
		fieldKey,
		fieldType,
		issueKey,
		isEditable,
		width,
		height,
		isSelected = false,
		wrapperRef,
		onFocus,
		onBlur,
		onKeyDown,
		fieldName = '',
		issueType = '',
		issueTypeId = '',
		editMode: editModeNew = false,
		setEditMode: setEditModeNew = noop,
		isHovering: isHoveringNew = false,
		cellRef,
	}: MultiUserCellProps) => {
		const initialValue = useMemo(
			() => _initialValue?.map(transformToUserOption).filter(Boolean) ?? null,
			[_initialValue],
		);

		const userPickerWidth = width > MENU_MIN_WIDTH ? width : MENU_MIN_WIDTH;
		const { formatMessage } = useIntl();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const [{ value: savedValue, error }, { saveValue }] = useEditField<UserOption[] | null, null>({
			fieldKey,
			issueKey,
			fieldType,
			initialValue,
			onFailure: () => {
				setUserOptions(savedValue ?? null);
			},
		});

		const [editMode, setEditMode] = fg('jira_list_keyboard_nav')
			? [editModeNew, setEditModeNew]
			: // eslint-disable-next-line react-hooks/rules-of-hooks
				useState<boolean>(false);
		const [isHoveringOld, setIsHovering] = useState(false);
		const isHovering = fg('jira_list_keyboard_nav') ? isHoveringNew : isHoveringOld;
		const [userOptions, setUserOptions] = useState<UserOption[] | null>(savedValue);
		const wasEditMode = usePreviousWithInitial(editMode);
		const hasValueUpdated = useRef(false);

		// Update the internal state in response to external data changes. Note
		// that setting state here is more efficient than with a `useEffect`
		// hook - see
		// https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
		if (
			!editMode &&
			!isUserOptionsEqual(savedValue, userOptions) &&
			(!fg('jira_list_keyboard_nav') || !wasEditMode)
		) {
			setUserOptions(savedValue);
		}

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleMouseEnter = useCallback(() => setIsHovering(true), [setIsHovering]);
		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleMouseLeave = useCallback(() => setIsHovering(false), [setIsHovering]);

		const handleChange = useCallback((userPickerValue: UserOption) => {
			let newValue: UserOption[] | null = null;
			if (userPickerValue) {
				newValue = Array.isArray(userPickerValue) ? userPickerValue : null;
			}
			setUserOptions(newValue);

			/**
			 * `hasValueUpdated` is used in keyboard navigation to check whether pressing Enter has changed the value,
			 * so that we know if we need to exit edit mode on `Enter` or not
			 */
			if (fg('jira_list_keyboard_nav')) {
				hasValueUpdated.current = true;

				setTimeout(() => {
					hasValueUpdated.current = false;
				});
			}
		}, []);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleCellClick = useCallback(() => {
			if (!editMode && isEditable) {
				setEditMode(true);
			}
		}, [editMode, isEditable, setEditMode]);

		const saveAndClose = useCallback(() => {
			setEditMode(false);

			if (isUserOptionsEqual(savedValue, userOptions)) {
				return;
			}

			saveValue(userOptions, null, createAnalyticsEvent({}));
		}, [createAnalyticsEvent, saveValue, savedValue, setEditMode, userOptions]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		useEffect(() => {
			if (!fg('jira_list_keyboard_nav') && isSelected === false && editMode) {
				saveAndClose();
			}
		}, [isSelected, editMode, saveAndClose]);

		const handleContentKeyDown = useCallback(
			(e: KeyboardEvent<Element>) => {
				if (e.key === 'Escape') {
					e.preventDefault();
					saveAndClose();
					wrapperRef?.current?.focus();
				}
			},
			[saveAndClose, wrapperRef],
		);

		useEffect(() => {
			if (wasEditMode && !editMode && fg('jira_list_keyboard_nav')) {
				saveAndClose();
			}
		}, [editMode, saveAndClose, wasEditMode]);

		const handleKeyDownNew = useCallback(
			(event: KeyboardEvent<HTMLElement>) => {
				if (editMode && event.code === 'Enter') {
					if (!hasValueUpdated.current) {
						setEditMode(false);
						return false;
					}

					return true;
				}

				return false;
			},
			[editMode, setEditMode],
		);

		const cellOverrides = useCallback(
			() => ({
				onKeyDown: handleKeyDownNew,
			}),
			[handleKeyDownNew],
		);
		useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const onWrapperRef = useCallback(
			(ref: ElementRef<'div'> | null) => {
				if (wrapperRef) {
					// eslint-disable-next-line no-param-reassign
					wrapperRef.current = ref;
				}
			},
			[wrapperRef],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleKeyDown = useCallback(
			(e: KeyboardEvent<HTMLDivElement>) => {
				e.stopPropagation();
				!editMode && onKeyDown?.(e);
			},
			[editMode, onKeyDown],
		);

		const handleCutCopy = useCallback(
			(event: ClipboardEvent<HTMLElement>) => {
				event.preventDefault();

				if (userOptions && userOptions.length > 0) {
					// copy plain text so can paste readable format anywhere
					event.clipboardData.setData(
						'text/plain',
						userOptions.map((user) => user.name).join(', '),
					);
					// copy html as we need to know the full UserOption value to paste multiple users
					event.clipboardData.setData(
						'text/html',
						userOptions
							.map(
								(user) =>
									`<span account-id="${user.id}" avatar-url="${user.avatarUrl}">${user.name}</span>`,
							)
							.join(''),
					);

					if (event.type === 'copy') {
						fireUIAnalytics(createAnalyticsEvent({}), 'cell copied', 'multi-user');
					} else if (event.type === 'cut') {
						fireUIAnalytics(createAnalyticsEvent({}), 'cell cut', 'multi-user');
						// clear cell content
						setUserOptions([]);
						saveValue([], null, createAnalyticsEvent({}));
					}
				}
			},
			[userOptions, createAnalyticsEvent, saveValue],
		);

		const handlePaste = useCallback(
			(event: ClipboardEvent<HTMLElement>) => {
				const clipHtml = event.clipboardData.getData('text/html');
				const clipText = event.clipboardData.getData('text/plain');

				if (clipHtml) {
					const parser = new DOMParser();
					const htmlDoc = parser.parseFromString(clipHtml, 'text/html');
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					const pastedUserOptions = Array.from(htmlDoc.querySelectorAll('span'))
						.map((span) => {
							const id = span.getAttribute('account-id');
							const avatarUrl = span.getAttribute('avatar-url') ?? undefined;
							const name = span.textContent;

							if (id && name) {
								return { id, name, avatarUrl };
							}
							return undefined;
						})
						.filter((value) => !!value) as UserOption[];

					if (pastedUserOptions.length > 0) {
						event.preventDefault();
						setUserOptions(pastedUserOptions);
						saveValue(pastedUserOptions, null, createAnalyticsEvent({}));
						fireUIAnalytics(createAnalyticsEvent({}), 'cell pasted', 'multi-user', {
							format: 'html',
						});
						return;
					}
				}

				if (clipText) {
					fireUIAnalytics(createAnalyticsEvent({}), 'cell pasted', 'multi-user', {
						format: 'text',
					});
				}
			},
			[saveValue, createAnalyticsEvent],
		);

		const popupTrigger = useCallback(
			(triggerProps: TriggerProps) => (
				<Pressable
					{...triggerProps}
					xcss={popupTriggerStyles}
					testId="business-list.ui.list-view.multi-user-cell.popup-trigger"
				>
					<MultiUserReadView isEditable={isEditable} userOptions={userOptions} />
				</Pressable>
			),
			[isEditable, userOptions],
		);

		const popupContent = useCallback(
			() => (
				<UserPicker
					width={width}
					height={height}
					userPickerWidth={userPickerWidth}
					menuMinWidth={MENU_MIN_WIDTH}
					placeholder={formatMessage(messages.placeholder)}
					fieldId={fieldType}
					value={userOptions}
					// @ts-expect-error - TS2322 - Type '(userPickerValue: UserOption) => void' is not assignable to type '() => void'.
					onChange={handleChange}
					onKeyDown={handleContentKeyDown}
					autoCompleteUrl={`/rest/api/3/user/recommend?${new URLSearchParams({
						context: 'CustomMultiUserField',
						...(issueKey && { issueKey }),
						query: '',
					})}`}
				/>
			),
			[
				issueKey,
				fieldType,
				formatMessage,
				handleChange,
				handleContentKeyDown,
				height,
				userOptions,
				userPickerWidth,
				width,
			],
		);

		const content =
			isHovering || editMode || isSelected ? (
				<Popup
					offset={fg('jira_list_keyboard_nav') ? POPUP_OFFSET_NEW : POPUP_OFFSET}
					placement="bottom-start"
					isOpen={editMode}
					onClose={saveAndClose}
					popupComponent={PopupComponent}
					autoFocus={false}
					trigger={popupTrigger}
					content={popupContent}
				/>
			) : (
				<MultiUserReadView isEditable={isEditable} userOptions={userOptions} />
			);

		const cellContent = (
			<>
				{isEditable ? (
					content
				) : (
					<MultiUserReadView isEditable={isEditable} userOptions={userOptions} />
				)}
				{error && (
					<FixItErrorFlag
						error={error}
						title={messages.errorTitle}
						description={messages.errorMessage}
						issueType={issueType}
						issueTypeId={issueTypeId}
						fieldId={fieldKey}
						fieldName={fieldName}
					/>
				)}
			</>
		);

		if (fg('jira_list_keyboard_nav')) {
			return (
				<CellWrapperNew
					isEditable={isEditable}
					testId="business-list.ui.list-view.multi-user-cell.cell-container"
					hasPadding={false}
					{...(!fg('jwm_list_new_copy_paste_behaviour')
						? {
								onCopy: handleCutCopy,
								onCut: handleCutCopy,
								onPaste: handlePaste,
							}
						: {})}
				>
					<ContentWrapper>{cellContent}</ContentWrapper>
				</CellWrapperNew>
			);
		}

		return (
			<CellContainer
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onClick={handleCellClick}
				isFocused={isSelected && !editMode}
				onBlur={onBlur}
				tabIndex={0}
				onFocus={onFocus}
				onKeyDown={handleKeyDown}
				ref={onWrapperRef}
				onCopy={handleCutCopy}
				onCut={handleCutCopy}
				onPaste={handlePaste}
				data-testid="business-list.ui.list-view.multi-user-cell.cell-container"
			>
				{cellContent}
			</CellContainer>
		);
	},
);

export default MultiUserCell;

const ContentWrapper = ({ children }: PropsWithChildren) => (
	<Box xcss={[wrapperStyles]}>{children}</Box>
);

const AvatarGroupContainer = ({
	children,
	isEditable,
	testId,
}: PropsWithChildren & { isEditable: boolean; testId: string }) => (
	<Flex
		testId={testId}
		alignItems="center"
		xcss={[
			avatarGroupContainerStyles,
			isEditable ? avatarGroupContainerPointerStyles : avatarGroupContainerDefaultStyles,
		]}
	>
		{children}
	</Flex>
);

const avatarGroupContainerStyles = xcss({
	paddingLeft: 'space.100',
	width: '100%',
});

const avatarGroupContainerDefaultStyles = xcss({
	cursor: 'default',
});

const avatarGroupContainerPointerStyles = xcss({
	cursor: 'pointer',
});

const wrapperStyles = xcss({
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AvatarGroupContainerOld = styled.div<{ isEditable: boolean }>({
	paddingLeft: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: ({ isEditable }) => (isEditable ? 'pointer' : 'default'),
	width: '100%',
});

const popupTriggerStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	padding: 'space.0',
});
